/*
  Custom element: <best-seller-card>
  Shared add-to-cart handler for the JitteryGit product card (snippets/pdp-product-card.liquid).
  Loaded by: best-sellers-homepage, pdp-recommendations ("You might also love"), pdp-related-products,
  shop-by-age-page-collection, main-collection-product-grid, main-pillar, main-article, main-blog,
  featured-blog, main-search.

  Intercepts the card's ATC form, shows Dawn's centered loading spinner, POSTs to routes.cart_add_url,
  re-renders every cart region on the page from that one response, then opens the drawer.

  ── ONE REQUEST, EVERY REGION, THIS PAGE'S CONTEXT ──────────────────────────────────────────────────
  docs/SCRIBBLEPOP_A4_BUILD.md §2.3, writer 5. This used to add, then fetch
  '/?sections=cart-drawer,cart-icon-bubble' in a SECOND request — rendered in homepage context, so on a
  Robotryx page the drawer came back with the light skin's markup — and it published nothing, so no
  subscriber ever heard the add. The add now carries CartRegions.sectionsToRender() with
  sections_url = this page, the contract cart.js, cart-attach-add.js and a4-cart.js already follow: the
  drawer, the bubble and the party-pack band (A4PartyPack, Session 2) re-render from one response.
  Swapping .drawer__inner rather than replacing #CartDrawer also keeps #CartDrawer-Overlay — and the
  close-on-click handler cart-drawer.js bound to it — alive (assets/cart-regions.js explains).

  It publishes cartUpdate with source 'cart-items' BECAUSE every region is already rendered: that value
  is the render-suppression flag, not provenance (read cart-attach-add.js's publish site). Any other
  value would send <cart-drawer-items> off to re-render the drawer from /cart — a wasted request that
  lands the wrong page context again. Provenance rides on origin: 'product-card'.

  Loading visual matches Dawn's .button.loading overlay (component-loading-overlay.css +
  .bsh__cta.loading rules in component-product-card.css) so every ATC button across the theme
  shows the same centered-spinner / hidden-text state.

  Tag name `best-seller-card` is historical (best-sellers-homepage origin); now shared.
*/
if (!customElements.get('best-seller-card')) {
  customElements.define(
    'best-seller-card',
    class BestSellerCard extends HTMLElement {
      connectedCallback() {
        if (this.classList.contains('bsh--clone')) return;
        this.querySelector('.bsh__atc-form')
          ?.addEventListener('submit', this.#onSubmit.bind(this));
      }

      async #onSubmit(e) {
        e.preventDefault();

        const btn     = this.querySelector('[data-add-btn]');
        const spinner = this.querySelector('.loading-overlay__spinner');

        if (btn?.getAttribute('aria-disabled') === 'true') return;

        btn?.setAttribute('aria-disabled', 'true');
        btn?.classList.add('loading');
        spinner?.classList.remove('hidden');

        /* Resolved before the fetch, like cart.js: the swap detaches the nodes it names. */
        const formData = new FormData(e.target);
        const regions  = window.CartRegions ? CartRegions.sectionsToRender() : [];
        if (regions.length) {
          formData.append('sections', regions.map((entry) => entry.section).join(','));
          formData.append('sections_url', window.location.pathname);
        }

        /* routes.cart_add_url has no .js; the Accept header is what returns JSON (product-form.js). */
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        config.body = formData;

        try {
          const res  = await fetch(`${routes.cart_add_url}`, config);
          const body = await res.json();
          if (!res.ok || body.status) throw new Error(body.description || 'add-to-cart failed');

          const cartDrawer = document.querySelector('cart-drawer');
          if (!cartDrawer || !regions.length) {
            window.location = routes.cart_url;
            return;
          }

          /* syncEmptyState AFTER the swap: the .drawer__inner render recreates <cart-drawer-items>. */
          CartRegions.renderSections(regions, body.sections);
          CartRegions.syncEmptyState(false);
          cartDrawer.open();

          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'cart-items',
            origin: 'product-card',
            productVariantId: formData.get('id')
          });

        } catch (err) {
          console.error('[BestSellerCard]', err);
        } finally {
          btn?.classList.remove('loading');
          btn?.removeAttribute('aria-disabled');
          spinner?.classList.add('hidden');
        }
      }
    }
  );
}
